import { useState } from "react";
import type { CoffeeInput } from "../types";
import { api } from "../lib/api";

interface Props {
  rows: Partial<CoffeeInput>[];
  onDone: (createdIds: number[]) => void;
  onCancel: () => void;
}

export function AiImportReview({ rows: initial, onDone, onCancel }: Props) {
  const [rows, setRows] = useState<Partial<CoffeeInput>[]>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setRow = (i: number, patch: Partial<CoffeeInput>) => {
    setRows((cur) => cur.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const removeRow = (i: number) => setRows((cur) => cur.filter((_, idx) => idx !== i));

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const ids: number[] = [];
      for (const r of rows) {
        if (!r.variety || !r.country || !r.roaster || !r.process) continue;
        const created = await api.create(r as CoffeeInput);
        ids.push(created.id);
      }
      onDone(ids);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-paper-700">Review {rows.length} parsed row(s) before importing. Edit cells inline; delete rows you don't want.</p>
      <div className="overflow-x-auto card">
        <table className="w-full text-sm">
          <thead className="bg-paper-100 text-paper-700 text-xs uppercase">
            <tr>
              <th className="text-left p-2">Variety</th>
              <th className="text-left p-2">Process</th>
              <th className="text-left p-2">Country</th>
              <th className="text-left p-2">Roaster</th>
              <th className="text-left p-2">Roast date</th>
              <th className="text-left p-2">Portions</th>
              <th className="text-left p-2">Grams</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-paper-200">
                <td className="p-1"><input className="input" value={r.variety ?? ""} onChange={(e) => setRow(i, { variety: e.target.value })} /></td>
                <td className="p-1"><input className="input" value={r.process ?? ""} onChange={(e) => setRow(i, { process: e.target.value })} /></td>
                <td className="p-1"><input className="input" value={r.country ?? ""} onChange={(e) => setRow(i, { country: e.target.value })} /></td>
                <td className="p-1"><input className="input" value={r.roaster ?? ""} onChange={(e) => setRow(i, { roaster: e.target.value })} /></td>
                <td className="p-1"><input className="input" type="date" value={r.roast_date ?? ""} onChange={(e) => setRow(i, { roast_date: e.target.value || null })} /></td>
                <td className="p-1"><input className="input" type="number" value={r.portions ?? 0} onChange={(e) => setRow(i, { portions: Number(e.target.value) })} /></td>
                <td className="p-1"><input className="input" type="number" step="0.1" value={r.grams ?? 15} onChange={(e) => setRow(i, { grams: Number(e.target.value) })} /></td>
                <td className="p-1"><button className="btn-ghost text-red-700" onClick={() => removeRow(i)}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {error ? <div className="text-red-700 text-sm">{error}</div> : null}
      <div className="flex gap-2 justify-end">
        <button className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button className="btn-primary" onClick={submit} disabled={busy || rows.length === 0}>
          {busy ? "Importing…" : `Import ${rows.length}`}
        </button>
      </div>
    </div>
  );
}
